import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button, 
  Typography 
} from '@mui/material';
import { api, endpoints, joinUrl } from '../api/config';

const ACCESS_TOKEN_EXPIRY = 30 * 60 * 1000; // 30 minutes
const CHECK_INTERVAL = 60 * 1000; // 1 minute
const BASE_URL = import.meta.env.VITE_API_URL;

export const useSessionManager = (token, login) => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('tokenCreationTime');
    sessionStorage.removeItem('username');
    navigate('/login', { state: { expired: true } });
  }, [navigate]);

  const handleTokenRefresh = useCallback(async () => {
    if (!token || isRefreshing) return;
    
    setIsRefreshing(true);
    sessionStorage.setItem('isRefreshing', 'true');
    console.log('[SessionManager] Starting silent background refresh...');
    
    try {
      // Use the standard api (axios) for the refresh call
      const response = await api.post(endpoints.refreshToken);
      const data = response.data?.data || response.data;
      
      if (data && data.access_token) {
        console.log('[SessionManager] Token refreshed successfully');
        // Update token in session storage
        sessionStorage.setItem('token', data.access_token);
        sessionStorage.setItem('tokenCreationTime', Date.now().toString());
        
        // Update application state with new token
        const username = sessionStorage.getItem('username');
        if (username) {
          login(data.access_token, { username });
        } else {
          login(data.access_token, data.user || { username: 'user' });
        }
      } else {
        console.error('[SessionManager] Refresh response missing access_token:', data);
        throw new Error('No token in response');
      }
    } catch (error) {
      console.error('[SessionManager] Failed to refresh token:', error);
      // Only logout if it's a 401/403 or we really can't recover
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
        // For network errors, we can potentially try again on the next interval
        console.warn('[SessionManager] Network issue during refresh, will retry on next check');
      }
    } finally {
      setIsRefreshing(false);
      sessionStorage.removeItem('isRefreshing');
    }
  }, [token, login, handleLogout, isRefreshing]);

  // Handle token expiry
  useEffect(() => {
    if (!token) return;

    const checkInterval = setInterval(() => {
      const now = Date.now();
      const tokenCreationTimeStr = sessionStorage.getItem('tokenCreationTime');
      const tokenCreationTime = tokenCreationTimeStr ? parseInt(tokenCreationTimeStr) : 0;
      
      if (!tokenCreationTime) {
        console.warn('[SessionManager] No tokenCreationTime found, skipping expiry check');
        return;
      }

      const timeUntilExpiry = ACCESS_TOKEN_EXPIRY - (now - tokenCreationTime);
      const threshold = 10 * 60 * 1000; // Trigger refresh 10 minutes before expiry

      // Silent Refresh: If token is about to expire in 10 minutes, trigger background refresh
      if (timeUntilExpiry <= threshold && timeUntilExpiry > 0 && !isRefreshing) {
        console.log(`[SessionManager] Token about to expire in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, triggering refresh...`);
        handleTokenRefresh();
      }

      // Check if token has expired
      if (timeUntilExpiry <= 0) {
        console.error('[SessionManager] Token has expired, logging out...');
        handleLogout();
      }
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(checkInterval);
    };
  }, [token, handleLogout, isRefreshing, handleTokenRefresh]);

  // Return empty component as refresh is now silent
  const SessionRefreshDialog = () => null;

  return { SessionRefreshDialog };
};
 